import { useEffect, useRef, useState, type KeyboardEvent, type MouseEventHandler } from "react";
import { Link } from "react-router-dom";
import { AvatarBadge } from "./AvatarBadge";

interface IdentityMenuProps {
  username: string;
  preferredName?: string | null;
  roleLabel: string;
  avatarUrl?: string | null;
  onLogout: () => void;
  onPasswordChangeClick?: MouseEventHandler<HTMLAnchorElement>;
  onMfaClick?: MouseEventHandler<HTMLAnchorElement>;
}

function focusMenuItem(items: HTMLElement[], current: EventTarget | null, direction: 1 | -1) {
  const currentIndex = items.findIndex((item) => item === current);
  const nextIndex = currentIndex === -1 ? 0 : (currentIndex + direction + items.length) % items.length;
  items[nextIndex]?.focus();
}

function handleMenuNavigation(event: KeyboardEvent<HTMLDivElement>, items: HTMLElement[]) {
  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
    event.preventDefault();
    focusMenuItem(items, event.target, event.key === "ArrowDown" ? 1 : -1);
    return true;
  }
  if (event.key === "Home" || event.key === "End") {
    event.preventDefault();
    items[event.key === "Home" ? 0 : items.length - 1]?.focus();
    return true;
  }
  if (event.key === "Tab") {
    event.preventDefault();
    focusMenuItem(items, event.target, event.shiftKey ? -1 : 1);
    return true;
  }
  return false;
}

export function IdentityMenu({ username, preferredName, roleLabel, avatarUrl, onLogout, onPasswordChangeClick, onMfaClick }: IdentityMenuProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const displayName = preferredName?.trim() || username;

  function closeMenu(restoreFocus = true) {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }

  function menuItems() {
    return Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
  }

  function openMenu() {
    setOpen(true);
  }

  function handleTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key !== "Enter" && event.key !== " " && event.key !== "ArrowDown") return;
    event.preventDefault();
    openMenu();
  }

  function handleMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      closeMenu();
      return;
    }

    const items = menuItems();
    if (items.length === 0) return;
    handleMenuNavigation(event, items);
  }

  useEffect(() => {
    if (!open) return;
    menuItems()[0]?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      closeMenu(false);
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  return (
    <div className="identity-menu">
      <button
        ref={triggerRef}
        type="button"
        className="identity-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Abrir menu da conta de ${displayName}, ${roleLabel}`}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={handleTriggerKeyDown}
      >
        <AvatarBadge name={displayName} username={username} avatarUrl={avatarUrl} alt={displayName} size="sm" />
      </button>

      {open && (
        <div ref={menuRef} className="identity-menu__panel" role="menu" aria-label="Menu de identidade" onKeyDown={handleMenuKeyDown}>
          <div className="identity-menu__profile">
            <AvatarBadge name={displayName} username={username} avatarUrl={avatarUrl} size="md" />
            <div>
              <strong>{displayName}</strong>
              <span>{roleLabel}</span>
            </div>
          </div>
          <Link
            role="menuitem"
            tabIndex={-1}
            to="/trocar-senha"
            onClick={(event) => {
              closeMenu(false);
              onPasswordChangeClick?.(event);
            }}
          >
            Alterar senha
          </Link>
          <Link
            role="menuitem"
            tabIndex={-1}
            to="/mfa/troca"
            onClick={(event) => {
              closeMenu(false);
              onMfaClick?.(event);
            }}
          >
            Trocar autenticador
          </Link>
          <Link
            role="menuitem"
            tabIndex={-1}
            to="/mfa/revogar"
            onClick={(event) => {
              closeMenu(false);
              onMfaClick?.(event);
            }}
          >
            Revogar autenticador
          </Link>
          <button
            role="menuitem"
            tabIndex={-1}
            type="button"
            onClick={() => {
              closeMenu(false);
              onLogout();
            }}
          >
            Sair
          </button>
        </div>
      )}
    </div>
  );
}
